import React, { useState } from "react";
import { Button, Grid, Paper, Typography, IconButton, List, ListItem, ListItemText, Box } from "@mui/material";
import { CheckCircleOutline, RadioButtonUnchecked } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import PasswordField from "../../field/PasswordField";
import { isEmptyValue } from "../../../helpers/methods";

export default function SetPasswordForm(props) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [formValue, setFormValue] = useState({
        password: undefined,
        confirmPassword: undefined
    });

    const requirements = [
        { id: 'length', label: t("form.set_password_form.min_length_8_chars"), test: (p) => p && p.length >= 8 },
        { id: 'number', label: t("form.set_password_form.at_least_one_number"), test: (p) => /\d/.test(p) },
        { id: 'letter', label: t("form.set_password_form.at_least_one_letter"), test: (p) => /[a-zA-Z]/.test(p) && !isEmptyValue(p) },
        { id: 'uppercase', label: t("form.set_password_form.at_least_one_uppercase"), test: (p) => /[A-Z]/.test(p) }, 
        { id: 'match', label: t("form.set_password_form.passwords_must_match"), test: (p, c) => p === c && !isEmptyValue(c) }
    ];

    const changeValue = (e) => {
        const { value, name } = e.target;
        setFormValue({ ...formValue, [name]: value });
    };


    const preventCopyPaste = (e) => {
        e.preventDefault();
        return false;
    };

    const allRequirementsMet = requirements.every(req => req.test(formValue.password, formValue.confirmPassword));

    return (
        <Paper elevation={3} className={"paper-form"} style={{ maxWidth: "600px", marginTop: "5vh", padding: "24px" }}>
            <Grid container direction="row" justifyContent="center" alignItems="flex-start" spacing={3}>
                <Grid item xs={12}>
                    <Typography
                        style={{ textTransform: "uppercase", color: "var(--primary)" }}
                        variant="h5"
                        display="block"
                        gutterBottom
                    >
                        {t("form.set_password_form.set_new_password")}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <PasswordField
                        name="password"
                        label={t("form.set_password_form.new_password")}
                        value={formValue.password}
                        onChange={changeValue}
                        onCopy={preventCopyPaste}
                        onCut={preventCopyPaste}
                        onPaste={preventCopyPaste}

                    />
                </Grid>

                <Grid item xs={12}>
                    <PasswordField
                        name="confirmPassword"
                        label={t("form.set_password_form.confirm_password")}
                        value={formValue.confirmPassword}
                        onChange={changeValue}
                        onCopy={preventCopyPaste}
                        onCut={preventCopyPaste}
                        onPaste={preventCopyPaste}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            {t("form.set_password_form.title")}:
                        </Typography>
                        <List dense>
                            {requirements.map((req) => {
                                const isMet = req.test(formValue.password, formValue.confirmPassword);
                                return (
                                    <ListItem key={req.id} sx={{ p: 0 }}>
                                        <IconButton size="small" disabled sx={{ p: '2px' }}>
                                            {isMet ? <CheckCircleOutline color="success" fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                                        </IconButton>
                                        <ListItemText
                                            primary={req.label}
                                            primaryTypographyProps={{ variant: 'caption', color: isMet ? 'success.main' : 'text.secondary' }}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Button
                        disabled={!allRequirementsMet}
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={() => props.onSubmit(formValue.password)}
                    >
                        {props.isAccountActivate ? t("form.set_password_form.activate_account") : t("form.set_password_form.save_new_password")}
                    </Button>
                </Grid>

                <Grid item xs={12}>
                    <Grid container direction="row" justifyContent="flex-end">
                        <Button size="small" variant="outlined" onClick={() => navigate('/')}>
                            {t("form.set_password_form.back")}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
}

SetPasswordForm.propTypes = {
    isAccountActivate: PropTypes.bool
};

SetPasswordForm.defaultProps = {
    isAccountActivate: false,
};